import {
  COUNTER_LAPTOP_OFFSET,
  type Fixture,
  type StaffSpot,
} from '../config/booth';
import { separate, stepAgent } from './behaviour';
import { type Box, distance, fixtureBox, pushOut } from './geometry';
import { createRng, type Rng } from './rng';
import { spawnAgent } from './spawn';
import type { Agent, KioskState, Point, Stats, World } from './types';

/** Attendees per minute entering the aisles at crowd level 1.0. */
const BASE_ARRIVALS_PER_MINUTE = 24;

/** Clearance bodies keep from furniture. */
const BODY_PAD = 0.6;

/** Kiosks and the counter (it carries a laptop) all take demos. */
function isDemoStation(f: Fixture): boolean {
  return f.kind === 'kiosk' || f.kind === 'counter';
}

/** Where a visitor stands to use the station: in front of its laptop, toward the aisle. */
function approachPoint(f: Fixture): Point {
  const laptopX =
    f.kind === 'counter' ? f.x - f.w * COUNTER_LAPTOP_OFFSET : f.x;
  return { x: laptopX, z: f.z + f.d / 2 + 1.1 };
}

function kioskStates(fixtures: Fixture[]): KioskState[] {
  return fixtures.filter(isDemoStation).map((f) => ({
    id: f.id,
    approach: approachPoint(f),
    occupant: null,
    queue: [],
    maxQueue: 0,
    demosDone: 0,
  }));
}

export class Simulation {
  readonly agents: Agent[] = [];
  readonly kiosks: KioskState[];
  readonly stats: Stats;
  /** 0 = empty hall, 1 = steady weekday, 2+ = opening-morning crush. */
  crowd = 1;
  private obstacles: Box[];
  private spawnDebt = 0;
  private readonly rng: Rng;

  constructor(
    public readonly world: World,
    seed = 1,
  ) {
    this.rng = createRng(seed);
    this.kiosks = kioskStates(world.fixtures);
    this.obstacles = world.fixtures.map((f) => fixtureBox(f, BODY_PAD));
    this.stats = {
      passedBy: 0,
      greeted: 0,
      missed: 0,
      inBooth: 0,
      demosDone: 0,
      maxQueue: Object.fromEntries(this.kiosks.map((k) => [k.id, 0])),
      queueNow: Object.fromEntries(this.kiosks.map((k) => [k.id, 0])),
    };
  }

  /** Call after dragging a fixture; queues and approach points follow it. */
  setFixtures(fixtures: Fixture[]): void {
    this.world.fixtures = fixtures;
    this.obstacles = fixtures.map((f) => fixtureBox(f, BODY_PAD));
    for (const k of this.kiosks) {
      const f = fixtures.find((x) => x.id === k.id);
      if (f) k.approach = approachPoint(f);
    }
  }

  setStaff(staff: StaffSpot[]): void {
    this.world.staff = staff;
  }

  /** Clears everyone and the counters; the layout stays. */
  reset(): void {
    this.agents.length = 0;
    for (const k of this.kiosks) {
      k.occupant = null;
      k.queue.length = 0;
      k.maxQueue = 0;
      k.demosDone = 0;
    }
    Object.assign(this.stats, {
      passedBy: 0,
      greeted: 0,
      missed: 0,
      inBooth: 0,
      demosDone: 0,
    });
    for (const id of Object.keys(this.stats.maxQueue)) {
      this.stats.maxQueue[id] = 0;
      this.stats.queueNow[id] = 0;
    }
    this.spawnDebt = 0;
  }

  update(dt: number): void {
    this.spawn(dt);
    const ctx = {
      world: this.world,
      kiosks: this.kiosks,
      obstacles: this.obstacles,
      rng: this.rng,
      dt,
    };
    for (const a of this.agents) stepAgent(a, ctx);
    separate(this.agents);
    this.keepOutOfFurniture();
    this.greet();
    this.retire();
    this.tally();
  }

  private spawn(dt: number): void {
    this.spawnDebt += (BASE_ARRIVALS_PER_MINUTE * this.crowd * dt) / 60;
    while (this.spawnDebt >= 1) {
      this.spawnDebt -= 1;
      this.agents.push(spawnAgent(this.rng, this.world));
    }
  }

  /** Separation can shove a body into a counter; put it back on the floor. */
  private keepOutOfFurniture(): void {
    for (const a of this.agents) {
      if (a.state === 'done') continue;
      for (const b of this.obstacles) {
        const p = pushOut(a, b);
        a.x = p.x;
        a.z = p.z;
      }
    }
  }

  private greet(): void {
    const { staff, staffReach } = this.world;
    for (const a of this.agents) {
      if (a.greeted || a.state === 'done' || a.state === 'walking') continue;
      if (staff.some((s) => distance(a, s) <= staffReach)) {
        a.greeted = true;
        this.stats.greeted += 1;
      }
    }
  }

  private retire(): void {
    for (let i = this.agents.length - 1; i >= 0; i--) {
      const a = this.agents[i]!;
      if (a.state !== 'done') continue;
      this.stats.passedBy += 1;
      if (!a.greeted && a.intent !== 'pass' && a.intent !== 'glance')
        this.stats.missed += 1;
      this.agents.splice(i, 1);
    }
  }

  private tally(): void {
    const { width, depth } = this.world;
    this.stats.inBooth = this.agents.filter(
      (a) => a.x >= 0 && a.x <= width && a.z >= 0 && a.z <= depth,
    ).length;
    this.stats.demosDone = this.kiosks.reduce((n, k) => n + k.demosDone, 0);
    for (const k of this.kiosks) {
      this.stats.maxQueue[k.id] = k.maxQueue;
      this.stats.queueNow[k.id] =
        k.queue.length + (k.occupant === null ? 0 : 1);
    }
  }
}
